import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { focusRing } from "../../design-system/focusRing";

// A connected account, as the prototype draws it: the integration's mark, its name,
// and its state carried by the chip's own colour rather than by a parenthesised word
// after the name.
const STATUSES = {
    ok: {
        border: "var(--border-hairline)",
        background: "var(--surface-card)",
        hoverBorder: "var(--border-strong)",
        color: "var(--text-heading)",
    },
    warning: {
        border: "var(--pw-warning-border)",
        background: "var(--pw-warning-bg)",
        hoverBorder: "var(--pw-warning-fg)",
        color: "var(--pw-warning-fg)",
    },
    error: {
        border: "var(--pw-danger-border)",
        background: "var(--pw-danger-bg)",
        hoverBorder: "var(--pw-danger-fg)",
        color: "var(--pw-danger-fg)",
    },
};

const Chip = styled.button`
    ${focusRing}
    display: flex;
    align-items: center;
    gap: var(--gap-l);
    width: 100%;
    min-height: var(--pw-control-height);
    padding: 0 18px;
    border: 1px solid ${(props) => STATUSES[props.$status].border};
    border-radius: var(--pw-radius-inner);
    background: ${(props) => STATUSES[props.$status].background};
    color: ${(props) => STATUSES[props.$status].color};
    font: inherit;
    font-size: var(--pw-text-base);
    font-weight: var(--pw-weight-regular);
    line-height: 1;
    text-align: left;
    cursor: pointer;
    overflow: hidden;
    transition: border-color var(--pw-transition-fast) var(--pw-ease);

    /* Hover sharpens the edge, it does not repaint the surface. Darkening the fill to the border colour put a
       strong tone behind the status badge, which keeps its own lighter fill — the chip then read as a box
       inside a box and the badge lost its contrast. */
    &:hover:not(:disabled) {
        border-color: ${(props) => STATUSES[props.$status].hoverBorder};
    }
`;

const Mark = styled.img`
    width: 18px;
    height: 18px;
    object-fit: contain;
    flex: none;
`;

const Glyph = styled.i`
    width: 18px;
    flex: none;
    font-size: 14px;
    text-align: center;
`;

const Title = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const PathwayConnectionChip = ({ title, logo, icon, status = "ok", onClick, dataCy, children }) => (
    <Chip type="button" $status={STATUSES[status] ? status : "ok"} onClick={onClick} data-cy={dataCy}>
        {logo ? <Mark src={logo} alt="" /> : icon && <Glyph className={icon} aria-hidden="true" />}
        <Title>{title}</Title>
        {children}
    </Chip>
);

PathwayConnectionChip.propTypes = {
    title: PropTypes.node.isRequired,
    logo: PropTypes.string,
    icon: PropTypes.string,
    status: PropTypes.oneOf(["ok", "warning", "error"]),
    onClick: PropTypes.func,
    dataCy: PropTypes.string,
    children: PropTypes.node,
};

export default PathwayConnectionChip;
